import type { Session } from './sessions.js';
import type { Goal, SessionWorkItem, Team } from './work-management.js';
import type { WorkspaceProjection } from './workspace-state.js';

export type SessionGrouping = 'goal' | 'team' | 'workspace' | 'user-initiated';

export const SESSION_GROUPINGS: readonly SessionGrouping[] = [
  'goal',
  'team',
  'workspace',
  'user-initiated',
] as const;

export const SESSION_GROUPING_LABELS: Record<SessionGrouping, string> = {
  goal: 'Goal › Project',
  team: 'Team › Goal',
  workspace: 'Workspace',
  'user-initiated': 'User-initiated',
};

export const ALL_FILTER = 'all';

export const CONVERSATIONS_GROUP_KEY = 'conversations';
export const CONVERSATIONS_GROUP_LABEL = 'Conversations';
export const UNLINKED_GROUP_KEY = 'unlinked';
export const UNLINKED_GROUP_LABEL = 'Unlinked work';
export const WORK_GROUP_KEY = 'agent-work';
export const WORK_GROUP_LABEL = 'Agent work';
export const NO_WORKSPACE_GROUP_KEY = 'no-workspace';
export const NO_WORKSPACE_GROUP_LABEL = 'No workspace';

export type SessionListStatusFilter = 'all' | 'active';
export type SessionListSort = 'last_activity' | 'created' | 'name';

export interface SessionRef {
  session_id: string;
  workspace_id: string | null;
}

export interface SessionListQuery {
  grouping: SessionGrouping;
  roleFilter: string;
  goalFilter: string;
  statusFilter: SessionListStatusFilter;
  workspaceFilter: string;
  sort: SessionListSort;
  showEmptyGroups: boolean;
}

export interface SessionListCatalogs {
  teams: Team[];
  goals: Goal[];
  workspaces: WorkspaceProjection[];
}

export interface SessionListBrowsePage {
  sessions: Session[];
  workItems: SessionWorkItem[];
  catalogs?: SessionListCatalogs;
  hasMore: boolean;
  nextOffset: number;
}

export interface SessionSubgroup<S> {
  key: string;
  label: string;
  sessions: S[];
}

export interface SessionGroup<S> {
  key: string;
  label: string;
  subgroups: SessionSubgroup<S>[];
  sessions: S[];
  isConversations: boolean;
}

export interface SessionGoalOption {
  id: string;
  label: string;
}

export interface SessionListView<S = Session> {
  groups: SessionGroup<S>[];
  roleOptions: string[];
  goalOptions: SessionGoalOption[];
}

export type SessionListFlatRow<S = Session> =
  | {
      type: 'group';
      key: string;
      label: string;
      loadedCount: number;
    }
  | {
      type: 'subgroup';
      key: string;
      label: string;
      loadedCount: number;
    }
  | {
      type: 'session';
      session: S;
      groupKey: string;
      depth: 1 | 2;
    };

export interface BulkDeleteItemFailure {
  item: SessionRef;
  error: string;
}

export interface DeleteManyResult {
  deleted: SessionRef[];
  failed: BulkDeleteItemFailure[];
}
